import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { categories, mainSiteUrl } from '@/mocks/categories';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileCatExpanded, setMobileCatExpanded] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const closeMobile = () => {
    setMobileMenuOpen(false);
    setMobileCatExpanded(false);
  };

  return (
    <nav
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16 md:h-[4.5rem]">
          <Link to="/" className="flex items-center gap-2 no-underline" onClick={closeMobile}>
            <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-500">
              <i className="ri-file-list-3-line text-background-50 text-lg"></i>
            </div>
            <span className="font-heading text-lg md:text-xl font-semibold text-foreground-900 whitespace-nowrap">
              VigorBuy Spreadsheet
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-1">
            <Link
              to="/"
              className="px-4 py-2 text-sm font-label text-foreground-700 hover:text-primary-600 transition-colors rounded-lg whitespace-nowrap cursor-pointer"
            >
              Home
            </Link>

            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="px-4 py-2 text-sm font-label text-foreground-700 hover:text-primary-600 transition-colors rounded-lg whitespace-nowrap cursor-pointer flex items-center gap-1"
              >
                Categories
                <i className={`ri-arrow-down-s-line text-xs transition-transform ${dropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {dropdownOpen && (
                <div className="absolute top-full left-0 mt-1 w-56 bg-background-50 rounded-lg border border-background-200/70 overflow-hidden max-h-80 overflow-y-auto">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer no-underline"
                    >
                      <div className="w-6 h-6 flex-shrink-0 rounded-md overflow-hidden">
                        <img src={cat.imageUrl} alt={cat.name} className="w-full h-full object-cover" />
                      </div>
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>

            <Link
              to="/vigorbuy-spreadsheet-guide"
              className="px-4 py-2 text-sm font-label text-foreground-700 hover:text-primary-600 transition-colors rounded-lg whitespace-nowrap cursor-pointer"
            >
              Shopping Guide
            </Link>

            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="ml-2 px-5 py-2 text-sm font-label bg-primary-500 text-background-50 rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer no-underline"
            >
              Visit Main Website
            </a>
          </div>

          <button
            onClick={() => {
              setMobileMenuOpen(!mobileMenuOpen);
              setMobileCatExpanded(false);
            }}
            className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg cursor-pointer"
          >
            <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-xl text-foreground-800`}></i>
          </button>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden border-t border-background-200/70 py-3 space-y-1 max-h-[70vh] overflow-y-auto">
            <Link
              to="/"
              onClick={closeMobile}
              className="flex items-center gap-3 px-4 py-3 text-sm font-label font-semibold text-foreground-800 hover:bg-background-100 rounded-lg cursor-pointer"
            >
              <div className="w-6 h-6 flex items-center justify-center">
                <i className="ri-home-4-line text-foreground-600"></i>
              </div>
              Home
            </Link>

            <Link
              to="/vigorbuy-spreadsheet-guide"
              onClick={closeMobile}
              className="flex items-center gap-3 px-4 py-3 text-sm font-label font-semibold text-foreground-800 hover:bg-background-100 rounded-lg cursor-pointer"
            >
              <div className="w-6 h-6 flex items-center justify-center">
                <i className="ri-book-open-line text-foreground-600"></i>
              </div>
              Shopping Guide
            </Link>

            <div>
              <button
                onClick={() => setMobileCatExpanded(!mobileCatExpanded)}
                className="w-full flex items-center justify-between px-4 py-3 text-sm font-label font-semibold text-foreground-800 hover:bg-background-100 rounded-lg cursor-pointer"
              >
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className="ri-grid-line text-foreground-600"></i>
                  </div>
                  Categories
                </div>
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className={`ri-arrow-down-s-line text-foreground-500 transition-transform ${mobileCatExpanded ? 'rotate-180' : ''}`}></i>
                </div>
              </button>

              {mobileCatExpanded && (
                <div className="mt-1 mx-2 p-2 rounded-lg bg-background-100 border border-background-200/50">
                  <div className="grid grid-cols-2 gap-1">
                    {categories.map((cat) => (
                      <a
                        key={cat.name}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        onClick={closeMobile}
                        className="flex items-center gap-2 px-2.5 py-2 rounded-md text-xs font-label text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer no-underline"
                      >
                        <div className="w-7 h-7 flex-shrink-0 rounded-md overflow-hidden">
                          <img src={cat.imageUrl} alt={cat.name} className="w-full h-full object-cover" />
                        </div>
                        <span className="truncate">{cat.name}</span>
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="px-4 pt-2 pb-1">
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                onClick={closeMobile}
                className="block w-full text-center px-5 py-3 text-sm font-label font-semibold bg-primary-500 text-background-50 rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer no-underline"
              >
                Visit Main Website
              </a>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}